import { useCallback, useEffect, useRef } from 'react'
import { useMutation } from 'react-query'

import { useAppDispatch, useAppSelector } from 'app/state'
import { FeatureFlag, useFeatureFlag } from 'app/ui/useFeatureFlag'

import useAccessToken from 'features/common/ui/useAccessToken'
import { syncUnlockMapOnUnload, takeOverMapAction } from 'features/maps/api/maps'
import { Map } from 'features/maps/types'
import { getCurrentMap } from 'features/maps/state/mapsSlice'

import { unlockMapRequest } from '../state/builderSlice'
import { BuilderLockState, BuilderMode } from '../types'
import { useCurrentUser } from 'features/users/hooks/userQueryHooks'
import { runAppSaga } from 'app/state/store'
import { takeOverCollaborativeMap } from '../state/collaborationSagas'
import { userCanEditMap } from 'features/maps/utils'

export function useCurrentCollaborativeMapInfo() {
  const currentMap = useAppSelector(getCurrentMap)
  return useCollaborativeMapInfo(currentMap)
}

export function useCollaborativeMapInfo(map: Map | undefined) {
  const currentUser = useCurrentUser()
  const currentUserId = currentUser?.user_id
  const currentMapLockStatus: BuilderLockState | undefined =
    useAppSelector((state) => state.builder.currentMapLockStatus) || map?.lockStatus
  const userHasEditMapPermission = map && currentUser && userCanEditMap(map, currentUser)
  const lockNeeded = userHasEditMapPermission && map?.collaborative

  const lockedByMe = currentMapLockStatus?.userId === currentUserId
  const takeOverNeeded = Boolean(lockNeeded && !lockedByMe && currentMapLockStatus?.locked === true)

  return {
    map,
    currentUser,
    currentMapLockStatus,
    userHasEditMapPermission,
    takeOverNeeded,
    locked: Boolean(currentMapLockStatus?.locked),
    lockedByMe,
    lockedByEmail: lockedByMe === false ? currentMapLockStatus?.email : undefined
  }
}

export function useTakeOverMutation() {
  const abortContollerRef = useRef<AbortController>()
  const cancel = useCallback(() => abortContollerRef.current?.abort(), [])

  return {
    ...useMutation(['builder', 'takeOverRequest'], () => {
      abortContollerRef.current = new AbortController()
      return runAppSaga(takeOverCollaborativeMap, abortContollerRef.current.signal)
    }),
    cancel,
    isCanceled: abortContollerRef.current?.signal?.aborted
  }
}

/**
 * Perform necessary steps to ensure that map can be edited.
 *
 * For normal maps, only check if user is owner of map.
 *
 * For collaborative maps, also allow other users with builder role also attempt to lock map.
 *
 * @returns canEdit - whether map can be edited
 */
export function useCollaborativeForEditing(map: Map | undefined, builderMode: BuilderMode) {
  const dispatch = useAppDispatch()

  const collaborativeMapsEnabled = useFeatureFlag(FeatureFlag.CollaborativeMaps)
  const lockNeeded = Boolean(collaborativeMapsEnabled && builderMode === BuilderMode.Editor && map?.collaborative)

  const currentUser = useCurrentUser({ enabled: lockNeeded })
  const token = useAccessToken({ enabled: lockNeeded })

  const currentUserId = currentUser?.user_id
  const currentMapLockStatus = useAppSelector((state) => state.builder.currentMapLockStatus) || map?.lockStatus

  const lockedByMe = Boolean(currentMapLockStatus?.locked) && currentMapLockStatus?.userId === currentUserId
  const mapId = map?.id
  const unlockNeeded = lockNeeded && lockedByMe

  // Unlock map (if needed), when navigation occurred
  useEffect(() => {
    if (!mapId || !unlockNeeded) return

    return () => {
      dispatch(unlockMapRequest(mapId))
    }
  }, [mapId, unlockNeeded, dispatch])

  // Attempt to unlock map (if neededs) on window unload.
  useEffect(() => {
    if (!unlockNeeded || !mapId || !token) {
      return
    }
    const handleUnload = () => {
      syncUnlockMapOnUnload({ mapId, token })
    }

    window.addEventListener('unload', handleUnload)
    return () => {
      window.removeEventListener('unload', handleUnload)
    }
  }, [unlockNeeded, mapId, token])
}

export function useTakeoverResponseAction() {
  return useMutation({
    mutationFn: (request: { mapId: string; action: 'request' | 'reject' | 'accept' }) => takeOverMapAction(request)
  })
}
